import type { ReactNode } from 'react';
import { Suspense, useEffect } from 'react';
import type { Theme } from '@mui/material';
import { CssBaseline, StyledEngineProvider } from '@mui/material';

import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { toast, ToastContainer } from 'react-toastify';

import { LoginProvider } from 'contexts/LoginProvider';
import { StorageProvider } from 'contexts/StorageProvider';
import { StoreProvider } from 'contexts/StoreProvider';
import { ChatWidgetProvider } from 'contexts/ChatWidgetContext';

import { Loader } from 'components/common';
import { Provider as ReduxProvider } from 'react-redux';
import { store } from 'store';
import { ThemeMuiProvider } from './ThemeMuiProvider';
import { ContextMenuProvider } from 'contexts/ContextMenuProvider.tsx';
import { BreadcrumbsProvider } from 'contexts/BreadcrumbsProvider.tsx';
import { ActionBlockerProvider } from '../../contexts/ActionBlockerProvider.tsx';
import { ShoppingCartProvider } from '../../contexts/ShoppingCartProvider.tsx';
import { NavigationBlockerProvider } from 'contexts/NavigationBlockerProvider';
import { DocumentEditingProvider } from 'contexts/DocumentEditingProvider';

const GoogleProvider = ({ children }: { children: ReactNode }) => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  return clientId ? <GoogleOAuthProvider clientId={clientId}>{children}</GoogleOAuthProvider> : <>{children}</>;
};

export const Providers = ({ children /*theme*/ }: { children: ReactNode; theme?: Theme }) => {
  useEffect(() => {
    return toast.onChange(({ id, type, status, data }) => {
      const preserveAutoClose = typeof data === 'object' && data !== null && 'preserveAutoClose' in data && data.preserveAutoClose === true;

      if (status === 'added' && type === 'success' && !preserveAutoClose) {
        toast.update(id, { autoClose: 2500 });
      }
    });
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <StorageProvider>
        <StoreProvider>
          <ReduxProvider store={store}>
            <HelmetProvider>
              <LoginProvider>
                <ChatWidgetProvider>
                  <ContextMenuProvider>
                    <BreadcrumbsProvider>
                      <ShoppingCartProvider>
                        <GoogleProvider>
                          <ThemeMuiProvider>
                            <CssBaseline />
                            <NavigationBlockerProvider>
                              <ActionBlockerProvider>
                                <DocumentEditingProvider>
                                  <Suspense fallback={<Loader />}>{children}</Suspense>
                                </DocumentEditingProvider>
                              </ActionBlockerProvider>
                            </NavigationBlockerProvider>
                            <ToastContainer newestOnTop position="bottom-right" stacked style={{ zIndex: 13000 }} />
                            <div id="modal-root" />
                          </ThemeMuiProvider>
                        </GoogleProvider>
                      </ShoppingCartProvider>
                    </BreadcrumbsProvider>
                  </ContextMenuProvider>
                </ChatWidgetProvider>
              </LoginProvider>
            </HelmetProvider>
          </ReduxProvider>
        </StoreProvider>
      </StorageProvider>
    </StyledEngineProvider>
  );
};
